import React, { useEffect } from 'react';
import { useState } from 'react';
import CategoryDropdownPosition from './CategoryDropdownPosition';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    return isMobile;
};

const StackedBarChart = ({phoneme, data_av, data_av_start, data_av_end, data_av_inside, data, data_start, data_inside, data_end, sample_end, sample, sample_inside, sample_start, isLoading, onPositionChange}) => {
    const [position, setPosition] = useState()
    const positions = ["All positions"]
    const [outdata, setOutdata] = useState(data)
    const [outdataav, setOutdataav] = useState(data_av)
    const [outsample, setSample] = useState(sample)
    const isMobile = useIsMobile();

    if (data_start){
        positions.push("start")
    }
    if (data_end){
        positions.push("end")
    }
    if (data_inside){
        positions.push("inside")
    }
    
    useEffect(() => {
        if (position) {
            if (position === 'start'){
                setOutdata(data_start)
                setOutdataav(data_av_start)
                setSample(sample_start) 
            }
            else if (position == "end"){
                setOutdata(data_end)
                setOutdataav(data_av_end)
                setSample(sample_end)
            }
            else if (position == "inside"){
                setOutdata(data_inside)
                setOutdataav(data_av_inside)
                setSample(sample_inside)
            }
            else{
                setOutdata(data)
                setOutdataav(data_av)
                setSample(sample)
            }
        }
    }, [position])

    const handlePositionChange = (e) => {
        setPosition(e.target.value);
        if (onPositionChange) {
            onPositionChange();
        }
    };

    const chartConfig = {
        margin: isMobile ? { left: 5, right: 5, top: 5, bottom: 5 } : { left: 20, right: 20, top: 5, bottom: 5 },
        fontSize: isMobile ? 10 : 12,
        yAxisWidth: isMobile ? 30 : 40
    };

    // Dimmed overlay for when parent is loading
    const ChartContainer = ({ children, title }) => (
        <div className={`bg-gray-50 rounded-lg p-4 border border-gray-200 relative ${isLoading ? 'opacity-50' : ''}`}>
            {isLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-70 rounded-lg z-10 flex items-center justify-center">
                    <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
                        <p className="text-blue-600 text-sm">Updating...</p>
                    </div>
                </div>
            )}
            <p className='text-sm font-semibold text-blue-600 mb-3'>{title}</p>
            {children}
        </div>
    );

    return (
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 w-full ${isLoading ? 'opacity-70' : ''}`}>
            {/* Header Section - Mobile Optimized */}
            <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4'>
                <div className='flex items-center gap-3 flex-1 min-w-0'>
                    <div className='bg-blue-100 p-2 sm:p-3 rounded-lg flex-shrink-0'>
                        <span className='text-xl sm:text-2xl font-bold text-blue-600'>{phoneme}</span>
                    </div>
                    <div className='min-w-0 flex-1'>
                        <p className='text-sm text-gray-600 truncate'>Position: {position || 'All'}</p>
                    </div>
                </div>
                <div className='flex-shrink-0 w-full sm:w-auto'>
                    <CategoryDropdownPosition 
                        selectHandler={handlePositionChange} 
                        elValue={position}
                        positions={positions}
                        disabled={isLoading}
                    />
                </div>
            </div>

            {/* Sample Text */}
            <div className={`bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200 ${isLoading ? 'opacity-50' : ''}`}>
                <p className='text-green-600 font-medium text-sm mb-1'>Sample Text:</p>
                <p className='text-lg font-semibold text-blue-800 break-words'>{outsample}</p>
            </div>

            {/* Charts Container - Vertical Stack on Mobile */}
            <div className="space-y-6">
                {/* Averaged Accuracy Chart */}
                <ChartContainer title="Averaged Accuracy Over Time">
                    <div className="w-full h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={outdataav} margin={chartConfig.margin}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis 
                                    dataKey="date" 
                                    tick={{ fontSize: chartConfig.fontSize }}
                                />
                                <YAxis 
                                    tick={{ fontSize: chartConfig.fontSize }}
                                    domain={[0, 100]}
                                    width={chartConfig.yAxisWidth}
                                />
                                <Tooltip />
                                <Legend />
                                <Bar 
                                    name="Daily Average" 
                                    dataKey="average" 
                                    fill="#3B82F6" 
                                    radius={[2, 2, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartContainer>

                {/* Distribution Chart */}
                <ChartContainer title="Performance Distribution">
                    <div className="w-full h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={outdata[1]} margin={chartConfig.margin}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis 
                                    dataKey="date" 
                                    tick={{ fontSize: chartConfig.fontSize }}
                                />
                                <YAxis 
                                    tick={{ fontSize: chartConfig.fontSize }}
                                    width={chartConfig.yAxisWidth}
                                />
                                <Tooltip />
                                <Bar name="80-100% (Excellent)" dataKey="green" stackId="a" fill="#10B981" />
                                <Bar name="40-80% (Good)" dataKey="yellow" stackId="a" fill="#F59E0B" />
                                <Bar name="0-40% (Needs Practice)" dataKey="red" stackId="a" fill="#EF4444" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartContainer>

                {/* Normalized Ratio Chart */}
                <ChartContainer title="Normalized Performance Ratio">
                    <div className="w-full h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={outdata[0]} margin={chartConfig.margin}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis 
                                    dataKey="date" 
                                    tick={{ fontSize: chartConfig.fontSize }}
                                />
                                <YAxis 
                                    tick={{ fontSize: chartConfig.fontSize }}
                                    width={chartConfig.yAxisWidth}
                                />
                                <Tooltip />
                                <Bar name="80-100% (Excellent)" dataKey="green" stackId="a" fill="#10B981" />
                                <Bar name="40-80% (Good)" dataKey="yellow" stackId="a" fill="#F59E0B" />
                                <Bar name="0-40% (Needs Practice)" dataKey="red" stackId="a" fill="#EF4444" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartContainer>
            </div>
        </div>
    )
}

export default StackedBarChart;